"use client"

import { useState, useCallback, useRef } from "react"
import { X, Printer, Loader2, AlertCircle } from "lucide-react"
import { LabelPrinter } from "./LabelPrinter"
import type { DynamicUser, DynamicSchema } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import { sendToPrinter } from "../services/printer"
import { updateDynamicUserByUniqueField } from "../services/api"
import { addToQueue } from "../services/queue"
import { updateDynamicUserInDB } from "../services/database"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { isHexColor } from "../utils/generateBadgeColor"
import type { LabelField, LabelFixedField } from "./LabelPrinter"

// Valor que se guarda en extra.printing_stations para impresiones del kiosco
const KIOSK_PRINTING_STATION = "kiosko"

// Reimpresión oculta para el asistente: un operador la habilita tocando el ícono
// de la impresora del encabezado REPRINT_UNLOCK_TAPS veces en menos de REPRINT_UNLOCK_WINDOW_MS
const REPRINT_UNLOCK_TAPS = 5
const REPRINT_UNLOCK_WINDOW_MS = 3000

interface KioskPrintModalProps {
  user: DynamicUser
  schema: DynamicSchema
  onClose: () => void
  onPrintSuccess: (updatedUser: DynamicUser) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  onClearSearch?: () => void
  cancelCurrentFetch?: () => void
}

const KioskPrintModal = ({
  user,
  schema,
  onClose,
  onPrintSuccess,
  showToast,
  cancelCurrentFetch
}: KioskPrintModalProps) => {
  const isOnline = useNetworkStatus()
  const [isPrinting, setIsPrinting] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)
  // Evita doble envío a la impresora mientras hay una impresión en curso
  const isPrintingRef = useRef(false)
  const alreadyPrinted = Boolean(user.printed)
  const [reprintUnlocked, setReprintUnlocked] = useState(false)
  const unlockTapsRef = useRef<number[]>([])

  // Función para reproducir sonido de éxito
  const playSuccessSound = () => {
    try {
      // Crear contexto de audio
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      // Sonido de éxito con secuencia musical ascendente
      const frequencies = [523.25, 659.25, 783.99, 1046.50] // Do, Mi, Sol, Do octava
      const duration = 0.15
      
      frequencies.forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        
        // Configurar sonido
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        oscillator.type = 'sine'
        
        // Configurar volumen con fade in/out
        const startTime = audioContext.currentTime + (index * duration)
        const endTime = startTime + duration
        
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.02)
        gainNode.gain.exponentialRampToValueAtTime(0.01, endTime)
        
        // Reproducir
        oscillator.start(startTime)
        oscillator.stop(endTime)
      })
      
    } catch (error) {
      // Fallback silencioso si no se puede reproducir audio
      console.log('Audio de éxito no disponible')
    }
  }

  // Primer valor no vacío entre varios nombres de campo (campos principales o extra)
  const getTextValue = useCallback((...fieldNames: string[]): string => {
    for (const fieldName of fieldNames) {
      const value = SchemaService.getFieldValue(user, fieldName)
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim()
      }
    }
    return ''
  }, [user])

  // Manejar impresión: primero la impresora local; solo si imprime se marca como impreso
  // (en el backend, marcar printed=1 dispara el QR first record)
  const handlePrint = useCallback(async () => {
    // Un gafete ya impreso solo se reimprime si un operador lo habilitó
    if (alreadyPrinted && !reprintUnlocked) return
    if (isPrintingRef.current) return
    isPrintingRef.current = true
    setIsPrinting(true)
    setPrintError(null)

    try {
      const uniqueField = SchemaService.getUniqueField(schema)
      const uniqueFieldValue = uniqueField ? SchemaService.getFieldValue(user, uniqueField) : null
      if (!uniqueFieldValue) {
        setPrintError("No se encontró el folio del asistente")
        return
      }

      const fixedFieldName = SchemaService.getFixedField(schema)
      const folio = (fixedFieldName ? getTextValue(fixedFieldName) : '') || String(uniqueFieldValue)
      const nombre = [getTextValue('name', 'nombre'), getTextValue('last_name', 'apellido')]
        .filter(Boolean)
        .join(' ')
      const puesto = getTextValue('cargo', 'puesto')
      const empresa = getTextValue('empresa', 'empresa_organizacion')

      // 1. Enviar a la impresora local
      console.log('[kiosk] enviando a impresora', { nombre, puesto, empresa, folio })
      const printResult = await sendToPrinter({ nombre, puesto, empresa, folio })
      if (!printResult.ok) {
        setPrintError(printResult.message)
        return
      }

      // 2. Marcar como impreso en local
      const updatedUser: DynamicUser = {
        ...user,
        printed: 1,
        extra: { ...user.extra, printing_stations: KIOSK_PRINTING_STATION },
      }
      cancelCurrentFetch?.()
      try {
        await updateDynamicUserInDB(updatedUser)
      } catch (error) {
        console.error('[kiosk] Error guardando impresión en IndexedDB:', error)
      }

      // 3. Marcar en el servidor en segundo plano (puede tardar por el QR first record);
      //    si falla o no hay conexión, se encola
      const updatePayload = {
        uniqueFieldValue: String(uniqueFieldValue),
        updateData: {
          printed: 1,
          extra: { printing_stations: KIOSK_PRINTING_STATION },
        },
      }
      const enqueue = () => addToQueue({
        type: "update_dynamic_user",
        data: updatePayload,
        timestamp: Date.now(),
      }).catch((error) => console.error('[kiosk] Error encolando impresión:', error))

      if (isOnline) {
        updateDynamicUserByUniqueField(updatePayload).catch((error) => {
          console.error('[kiosk] Error marcando impreso en servidor, se encola:', error)
          enqueue()
        })
      } else {
        enqueue()
      }

      playSuccessSound()
      showToast("success", alreadyPrinted ? "¡Gafete reimpreso correctamente!" : "¡Gafete impreso correctamente!")
      window.dispatchEvent(new CustomEvent('clear-kiosk-search'))
      onPrintSuccess(updatedUser)
    } catch (error) {
      console.error('[kiosk] Error durante la impresión:', error)
      setPrintError("Error durante la impresión")
    } finally {
      isPrintingRef.current = false
      setIsPrinting(false)
    }
  }, [user, schema, isOnline, alreadyPrinted, reprintUnlocked, getTextValue, onPrintSuccess, showToast, cancelCurrentFetch])

  // Toques del operador sobre el ícono del encabezado para habilitar la reimpresión
  const handleUnlockTap = useCallback(() => {
    if (!alreadyPrinted || reprintUnlocked) return
    const now = Date.now()
    const taps = [...unlockTapsRef.current.filter((time) => now - time < REPRINT_UNLOCK_WINDOW_MS), now]
    unlockTapsRef.current = taps
    if (taps.length >= REPRINT_UNLOCK_TAPS) {
      unlockTapsRef.current = []
      setReprintUnlocked(true)
    }
  }, [alreadyPrinted, reprintUnlocked])

  // Manejar cierre del modal con evento personalizado
  const handleClose = useCallback(() => {
    onClose()
    // Disparar evento personalizado para limpiar búsqueda
    window.dispatchEvent(new CustomEvent('clear-kiosk-search'))
  }, [onClose])


  // Convertir schema dinámico a campos de LabelPrinter - MISMO QUE DynamicPrintModal
  const convertToLabelFields = useCallback((): LabelField[] => {
    return Object.entries(schema)
      .filter(([_, fieldSchema]) => fieldSchema.print === true)
      .filter(([_, fieldSchema]) => !fieldSchema.fixed) // Excluir campo fixed (va separado)
      .sort((a, b) => (a[1].position || 999) - (b[1].position || 999))
      .map(([fieldName, fieldSchema]) => ({
        name: fieldName,
        label: fieldSchema.label,
        value: SchemaService.getFieldValue(user, fieldName)?.toString() || '',
        type: fieldSchema.type === 'number' ? 'number' : 
              fieldSchema.type === 'enum' ? 'enum' : 'text',
        options: fieldSchema.options,
        required: fieldSchema.required,
        editable: false // En kiosco no se permite edición
      }))
  }, [user, schema])

  // Obtener campo fijo para QR - MISMO QUE DynamicPrintModal
  const getFixedField = useCallback((): LabelFixedField | undefined => {
    const fixedFieldName = SchemaService.getFixedField(schema)
    
    if (!fixedFieldName) return undefined
    
    const fixedValue = SchemaService.getFieldValue(user, fixedFieldName)
    if (!fixedValue) return undefined

    return {
      value: String(fixedValue),
      label: schema[fixedFieldName]?.label || 'Código QR',
      editable: false // No editable en kiosco
    }
  }, [user, schema])

  // Obtener categoría para watermark - MISMO QUE DynamicPrintModal
  const getCategoryValue = useCallback((): string => {
    return SchemaService.getFieldValue(user, 'categoria') || 
           SchemaService.getFieldValue(user, 'category') || 
           ''
  }, [user])

  const labelFields = convertToLabelFields()
  const fixedField = getFixedField()
  const category = String(getCategoryValue() || '').trim()
  const badgeColor = getTextValue('gafete_color', 'badge_color')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {(isPrinting || printError) && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4 min-w-[280px] max-w-sm">
            {printError ? (
              <>
                <AlertCircle className="h-12 w-12 text-red-500" />
                <p className="text-lg font-semibold text-gray-800">Error de impresión</p>
                <p className="text-sm text-red-600 text-center">{printError}</p>
                <div className="mt-2 flex gap-3">
                  <button
                    onClick={handlePrint}
                    className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors"
                  >
                    Reintentar
                  </button>
                  <button
                    onClick={() => setPrintError(null)}
                    className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            ) : (
              <>
                <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
                <p className="text-lg font-semibold text-gray-800">Enviando a impresora...</p>
                <p className="text-sm text-gray-500">Por favor espere</p>
              </>
            )}
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {/* Acceso de operador: tocar el ícono varias veces habilita la reimpresión */}
            <div
              onClick={handleUnlockTap}
              className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center select-none touch-manipulation"
            >
              <Printer className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Vista Previa del Gafete
              </h2>
              <p className="text-sm text-gray-600">
                Verifica los datos antes de imprimir
              </p>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            disabled={isPrinting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
            title="Cerrar"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status del usuario, categoría y color de gafete */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-slate-50 p-5 shadow-sm">
            <div className="mb-4 flex flex-col items-center">
              <div className="flex items-center justify-center space-x-2">
                {alreadyPrinted ? (
                  <>
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    <span className="font-medium text-green-800">Gafete ya impreso anteriormente</span>
                  </>
                ) : (
                  <>
                    <div className="h-3 w-3 rounded-full bg-orange-500"></div>
                    <span className="font-medium text-orange-800">Gafete pendiente de impresión</span>
                  </>
                )}
              </div>
              {alreadyPrinted && !reprintUnlocked && (
                <p className="mt-1 text-sm text-gray-600">Si necesitas una reimpresión, acude con un operador.</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">Categoría</div>
                <div className="inline-flex max-w-full items-center rounded-lg bg-white px-2.5 py-1.5 text-sm font-medium text-indigo-900 shadow-sm">
                  <span className="truncate">{category || "Sin categoría"}</span>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">Color de Gafete</div>
                {badgeColor && isHexColor(badgeColor) ? (
                  <div className="inline-flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 shadow-sm">
                    <span
                      className="h-5 w-5 rounded-md border border-slate-300"
                      style={{ backgroundColor: badgeColor }}
                      title={badgeColor.toUpperCase()}
                    />
                    <span className="font-mono text-sm font-semibold text-slate-800">{badgeColor.toUpperCase()}</span>
                  </div>
                ) : (
                  <div className="inline-flex rounded-lg bg-white px-2.5 py-1.5 text-sm font-medium text-amber-600 shadow-sm">
                    {badgeColor ? `Color inválido: ${badgeColor}` : "Sin color de gafete"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Label Preview - CON CONTROLES PARA IMPRIMIR */}
          <div className="mb-6">
            <LabelPrinter
              fields={labelFields}
              fixedField={fixedField}
              category={category}
              size={{ width: 90, height: 29, dpi: 300, margin: 2, contentScale: 0.82 }}
              onPrint={handlePrint}
              showControls={true}
              allowEdit={false}
              kioskMode={true}
              alreadyPrinted={alreadyPrinted}
              reprintUnlocked={reprintUnlocked}
              className="w-full"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={handleClose}
            disabled={isPrinting}
            className="px-6 py-3 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 transition-all duration-300 disabled:opacity-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default KioskPrintModal
